import { useState, type ReactNode, type CSSProperties, type ButtonHTMLAttributes } from 'react';
import './ui.css';

/* ── Card ────────────────────────────────────────────────────────────── */
export function Card({ children, style, padded = true, tone = 'paper' }: {
  children: ReactNode; style?: CSSProperties; padded?: boolean; tone?: 'paper' | 'deep';
}) {
  return (
    <div className={`nb-card ${padded ? 'nb-card--padded' : ''} ${tone === 'deep' ? 'nb-card--deep' : ''}`} style={style}>
      {children}
    </div>
  );
}

/* ── PageHeader ──────────────────────────────────────────────────────── */
export function PageHeader({ eyebrow, title, lede, actions }: {
  eyebrow: string; title: ReactNode; lede?: ReactNode; actions?: ReactNode;
}) {
  return (
    <div className="nb-pageheader">
      <div className="nb-pageheader__text">
        <div className="nb-pageheader__eyebrow">{eyebrow}</div>
        <h1 className="nb-pageheader__title">{title}</h1>
        {lede && <p className="nb-pageheader__lede">{lede}</p>}
      </div>
      {actions && <div className="nb-pageheader__actions">{actions}</div>}
    </div>
  );
}

/* ── Button ──────────────────────────────────────────────────────────── */
type BtnTone = 'ember' | 'ink' | 'ghost' | 'danger';
interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: BtnTone;
  size?: 'sm' | 'md';
}
export function Button({ tone = 'ghost', size = 'md', className = '', ...rest }: BtnProps) {
  return <button {...rest} className={`nb-btn nb-btn--${tone} nb-btn--${size} ${className}`} />;
}

/* ── StatusPill ──────────────────────────────────────────────────────── */
export function StatusPill({ tone, children }: { tone: 'moss' | 'ember' | 'rust' | 'brass'; children: ReactNode }) {
  return <span className={`nb-pill nb-pill--${tone}`}><span className="nb-pill__dot" />{children}</span>;
}

/* ── Stamp ───────────────────────────────────────────────────────────── */
export function Stamp({ tone = 'ember', tilt = -2, children }: { tone?: 'ember' | 'moss' | 'rust' | 'ink'; tilt?: number; children: ReactNode }) {
  return <span className={`nb-stamp nb-stamp--${tone}`} style={{ transform: `rotate(${tilt}deg)` }}>{children}</span>;
}

/* ── KpiCard ─────────────────────────────────────────────────────────── */
export function KpiCard({ caption, value, delta, trend, footer }: {
  caption: string;
  value: ReactNode;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  trend?: number[];
  footer?: ReactNode;
}) {
  return (
    <div className="nb-kpi">
      <div className="nb-kpi__cap">{caption}</div>
      <div className="nb-kpi__num">{value}</div>
      <div className="nb-kpi__rule" />
      {delta && (
        <div className={`nb-kpi__delta nb-kpi__delta--${delta.direction}`}>
          {delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '◆'} {delta.value}
        </div>
      )}
      {trend && trend.length > 1 && <Sparkline values={trend} />}
      {footer && <div className="nb-kpi__foot">{footer}</div>}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const w = 240, h = 36, p = 2;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = Math.max(1, max - min);
  const step = (w - p * 2) / Math.max(1, values.length - 1);
  const pts = values.map((v, i) => {
    const x = p + i * step;
    const y = p + (h - p * 2) - ((v - min) / range) * (h - p * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg className="nb-kpi__spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden>
      <polyline points={pts} fill="none" stroke="var(--ember)" strokeWidth="1.5" />
    </svg>
  );
}

/* ── Spinner ─────────────────────────────────────────────────────────── */
export function Spinner({ size = 14 }: { size?: number }) {
  return <span className="nb-spin" style={{ width: size, height: size }} aria-label="loading" />;
}

/* ── EmptyState ──────────────────────────────────────────────────────── */
export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="nb-empty">
      <div className="nb-empty__rule" />
      <div className="nb-empty__title">{title}</div>
      {hint && <div className="nb-empty__hint">{hint}</div>}
      {action && <div className="nb-empty__action">{action}</div>}
    </div>
  );
}

/* ── Input ───────────────────────────────────────────────────────────── */
export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`nb-input ${props.className || ''}`} />;
}
export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`nb-input nb-input--ta ${props.className || ''}`} />;
}
export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`nb-input nb-input--sel ${props.className || ''}`} />;
}
export function Checkbox({ label, hint, tone, checked, onChange }: {
  label: ReactNode;
  hint?: string;
  tone?: 'danger';
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`nb-check ${tone === 'danger' ? 'nb-check--danger' : ''}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="nb-check__body">
        <span className="nb-check__label">{label}</span>
        {hint && <span className="nb-check__hint">{hint}</span>}
      </span>
    </label>
  );
}
export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="nb-field">
      <span className="nb-field__label">{label}</span>
      {children}
      {hint && <span className="nb-field__hint">{hint}</span>}
    </label>
  );
}

/* ── Accordion ───────────────────────────────────────────────────────── */
export function Accordion({
  title, subtitle, badge, defaultOpen = false, children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`nb-acc ${open ? 'is-open' : ''}`}>
      <button type="button" className="nb-acc__head" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span className="nb-acc__chev" aria-hidden>{open ? '▾' : '▸'}</span>
        <span className="nb-acc__title">{title}</span>
        {subtitle && <span className="nb-acc__sub">{subtitle}</span>}
        {badge && <span className="nb-acc__badge">{badge}</span>}
      </button>
      {open && <div className="nb-acc__body">{children}</div>}
    </div>
  );
}

/* ── ReadOnlyText — multi-line read-only content block ───────────────── */
export function ReadOnlyText({ value, placeholder }: { value?: string; placeholder?: string }) {
  if (!value || !value.trim()) {
    return <div className="nb-ro nb-ro--empty"><em>{placeholder || 'No content saved yet.'}</em></div>;
  }
  return <div className="nb-ro">{value}</div>;
}
